import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

import {
  Header as HeaderTitle,
  Description,
  Wrapper,
  Pencil,
  EditInput,
  Form,
  PreviewHeader,
} from './DocumentHeader.styles'

type EditReturn = Promise<any> | void
type EditHandler = (title: string) => EditReturn

interface HeaderInputProps {
  value: string
  placeholder?: string
  onEdit: EditHandler
  onCancel: () => void
}

const HeaderInput: React.FC<HeaderInputProps> = props => {
  const { value: externalValue, onEdit, placeholder, onCancel } = props

  const [value, setValue] = useState(externalValue)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    textAreaRef.current?.select()
  }, [])

  // Make the text area appear selected when opened and
  // mount the escape button hook
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [onCancel])

  const createSubmit = (form: HTMLFormElement | null) => {
    form?.dispatchEvent(new Event('submit', { cancelable: true }))
  }

  return (
    <>
      <PreviewHeader>{value}</PreviewHeader>
      <Form
        onSubmit={e => {
          e.preventDefault()
          onEdit(value.trim())
        }}>
        <EditInput
          placeholder={placeholder}
          onChange={e => setValue(e.target.value)}
          onBlur={e => {
            createSubmit(e.currentTarget.form)
          }}
          onKeyPress={e => {
            // Make the "Enter" key submit the change instead of a new line
            if (e.key === 'Enter') {
              e.preventDefault()
              createSubmit(e.currentTarget.form)
            }
          }}
          ref={textAreaRef}
          value={value}
        />
      </Form>
    </>
  )
}

interface DocumentHeaderProps {
  className?: string
  title: string
  titlePlaceholder?: string
  description?: string
  onEdit?: EditHandler
}

const DocumentHeader: React.FC<DocumentHeaderProps> = props => {
  const { className, title, description, onEdit, titlePlaceholder } = props
  const [isEditing, setEditing] = useState(false)

  const headerRef = useRef<HTMLHeadingElement>(null)
  let header = <HeaderTitle>{title}</HeaderTitle>

  if (isEditing && onEdit) {
    header = (
      <HeaderInput
        value={title}
        placeholder={titlePlaceholder}
        onEdit={async newTitle => {
          if (title !== newTitle) {
            await onEdit(newTitle)
          }

          setEditing(false)

          // Make the button gain focus again after the change
          setTimeout(() => {
            headerRef.current?.focus()
          })
        }}
        onCancel={() => {
          setEditing(false)
        }}
      />
    )
  } else if (onEdit) {
    header = (
      <HeaderTitle
        tabIndex={0}
        role="heading button"
        ref={headerRef}
        onClick={() => setEditing(true)}
        onKeyPress={e =>
          ['Enter', 'Space'].includes(e.key) && setEditing(true)
        }>
        {title}
        <Pencil />
      </HeaderTitle>
    )
  }

  return (
    <Wrapper className={className}>
      {header}
      {description && <Description>{description}</Description>}
    </Wrapper>
  )
}

export default styled(DocumentHeader)``
